import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '@/lib/store';
import { sites as sitesApi, auth } from '@/lib/api';
import type { Site } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';

export function DashboardPage() {
  const { isAuthenticated, setUser, sites, setSites } = useStore();
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [email, setEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  useEffect(() => {
    checkAuthAndLoadSites();
  }, []);

  const checkAuthAndLoadSites = async () => {
    try {
      const { user: currentUser } = await auth.getMe();
      setUser(currentUser);
      await loadSites();
    } catch {
      setShowAuthModal(true);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const { sites: userSites } = await sitesApi.list();
      setSites(userSites);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  const handleRequestMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthMessage(null);

    try {
      await auth.requestMagicLink(email);
      setAuthMessage('Check your email for a login link!');
    } catch (err) {
      setAuthMessage(err instanceof Error ? err.message : 'Failed to send login link');
    } finally {
      setAuthLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" className="text-primary-500" />
      </div>
    );
  }

  if (showAuthModal && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Sign In to Dashboard</h1>
            <p className="text-gray-600">
              Enter your email to receive a magic login link.
            </p>
          </div>

          <div className="card">
            <form onSubmit={handleRequestMagicLink} className="space-y-4">
              <div>
                <label htmlFor="email" className="label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {authMessage && (
                <div className={`p-4 rounded-lg ${authMessage.includes('Check') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {authMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={authLoading}
                className="btn-primary w-full"
              >
                {authLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2 text-white" />
                    Sending...
                  </>
                ) : (
                  'Send Magic Link'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-gray-500">
                Don't have a site yet?
              </p>
              <Link to="/generate" className="text-primary-600 hover:text-primary-700 font-medium">
                Create your first landing page
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Manage your landing pages and view analytics.</p>
          </div>
          <Link to="/generate" className="btn-primary">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Page
          </Link>
        </div>

        {sites.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No landing pages yet</h2>
            <p className="text-gray-600 mb-6">Create your first custom landing page in minutes.</p>
            <Link to="/generate" className="btn-primary">
              Create Your First Page
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.map((site) => (
              <SiteCard key={site.id} site={site} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SiteCard({ site }: { site: Site }) {
  const statusColors = {
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    deleted: 'bg-red-100 text-red-700',
  };

  return (
    <Link to={`/dashboard/sites/${site.id}`} className="card-hover">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{site.settings.businessName}</h3>
          <p className="text-sm text-gray-500">{site.settings.industry}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[site.status as keyof typeof statusColors] || statusColors.active}`}>
          {site.status}
        </span>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center text-gray-600">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <a
            href={site.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-primary-600 hover:underline truncate"
          >
            {site.liveUrl.replace('https://', '')}
          </a>
        </div>

        <div className="flex items-center text-gray-500">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Created {new Date(site.createdAt).toLocaleDateString()}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t flex justify-between text-sm">
        <span className="text-gray-500 capitalize">{site.plan} plan</span>
        <span className="text-primary-600 font-medium">View Details →</span>
      </div>
    </Link>
  );
}
